import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import { api } from '../../services/api';
import Header from '../Common/Header';
import './WinnerRunnerUpPage.css';

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const base = process.env.REACT_APP_API_URL?.replace('/api', '') || '';
  const clean = url.startsWith('/') ? url.substring(1) : url;
  return `${base}/${clean}`;
};

const fallbackPhoto = "https://via.placeholder.com/100/FFD700/333333?text=👤";
const fallbackSymbol = "https://via.placeholder.com/60/fff/333333?text=⦿";

const WinnerRunnerUpPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate(); // Initialize navigate
  const [constituencyResults, setConstituencyResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchResults = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.getAllCandidatesWithResults();
        if (res && res.success && Array.isArray(res.data)) {
          const firstItem = res.data[0];
          let processedData;
          if (firstItem && firstItem.candidates && Array.isArray(firstItem.candidates)) {
            processedData = res.data.sort((a, b) => {
              const numA = parseInt(a.constituencyCode?.match(/\d+/)?.[0] || 0);
              const numB = parseInt(b.constituencyCode?.match(/\d+/)?.[0] || 0);
              return numA - numB;
            });
          } else {
            processedData = groupByConstituency(res.data);
          }
          setConstituencyResults(processedData);
          setFilteredResults(processedData);
        } else {
          setConstituencyResults([]);
          setFilteredResults([]);
          setError(t('winnerRunnerUp.noData'));
        }
      } catch (error) {
        setConstituencyResults([]);
        setFilteredResults([]);
        setError(error.message || t('winnerRunnerUp.failedToLoad'));
      } finally {
        setLoading(false);
      }
    };
    fetchResults();
    // eslint-disable-next-line
  }, [t]);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredResults(constituencyResults);
      return;
    }
    const searchLower = searchTerm.toLowerCase();
    const filtered = constituencyResults.filter(constituency => {
      const matchesConstituency =
        constituency.constituencyName?.toLowerCase().includes(searchLower) ||
        constituency.constituencyCode?.toLowerCase().includes(searchLower);
      const matchesCandidates = constituency.candidates?.some(candidate =>
        (candidate.candidateName || candidate.name || '').toLowerCase().includes(searchLower) ||
        (candidate.partyName || '').toLowerCase().includes(searchLower) ||
        (candidate.partyShortName || '').toLowerCase().includes(searchLower)
      );
      return matchesConstituency || matchesCandidates;
    });
    setFilteredResults(filtered);
  }, [searchTerm, constituencyResults]);

  const groupByConstituency = (candidatesArray) => {
    const grouped = candidatesArray.reduce((acc, candidate) => {
      const key = candidate.constituencyId;
      if (!acc[key]) {
        acc[key] = {
          constituencyId: candidate.constituencyId,
          constituencyName: candidate.constituencyName || t('winnerRunnerUp.unknown'),
          constituencyCode: candidate.constituencyCode || '',
          candidates: []
        };
      }
      acc[key].candidates.push(candidate);
      return acc;
    }, {});
    return Object.values(grouped).sort((a, b) => {
      const numA = parseInt(a.constituencyCode?.match(/\d+/)?.[0] || 0);
      const numB = parseInt(b.constituencyCode?.match(/\d+/)?.[0] || 0);
      return numA - numB;
    });
  };

  const resolvePhoto = (c) =>
    getImageUrl(
      c?.photoUrl || c?.photo || c?.candidatePhoto || c?.candidateImage || c?.image || ""
    ) || fallbackPhoto;

  const resolveSymbol = (c) =>
    getImageUrl(
      c?.symbolUrl || c?.partySymbol || c?.partyLogo || c?.symbol || c?.logoUrl || c?.logo || ""
    ) || fallbackSymbol;

  const getWinnerAndRunnerUp = (candidatesInput) => {
    if (!Array.isArray(candidatesInput) || candidatesInput.length === 0) {
      return { winner: null, runnerUp: null };
    }
    const enriched = candidatesInput.map(candidate => {
      const rounds = Array.isArray(candidate.rounds) ? candidate.rounds : [];
      const latestRound = rounds.length > 0 ? rounds[rounds.length - 1] : {};
      const latestVotes = typeof latestRound.votes === 'number' ? latestRound.votes : 0;
      return { ...candidate, latestVotes };
    }).sort((a, b) => b.latestVotes - a.latestVotes);
    return { winner: enriched[0] || null, runnerUp: enriched[1] || null };
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="winner-runnerup-page">
          <div className="page-header">
            <h1>🏆 {t('winnerRunnerUp.title')}</h1>
            <p className="subtitle">{t('winnerRunnerUp.subtitle')}</p>
          </div>
          <div className="loading-container">
            <div className="spinner"></div>
            <p>{t('winnerRunnerUp.loading')}</p>
          </div>
        </div>
      </>
    );
  }
  if (error) {
    return (
      <>
        <Header />
        <div className="winner-runnerup-page">
          <div className="page-header">
            <h1>🏆 {t('winnerRunnerUp.title')}</h1>
          </div>
          <div className="error-container">
            <span className="error-icon">⚠️</span>
            <p className="error-message">{error}</p>
            <button onClick={() => window.location.reload()} className="retry-btn">
              {t('winnerRunnerUp.retry')}
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="winner-runnerup-page">
        <div className="page-header">
          <h1>🏆 {t('winnerRunnerUp.title')}</h1>
          <p className="subtitle">{t('winnerRunnerUp.subtitle')}</p>
        </div>

        <div className="controls-section">
          <div className="controls-bar">
            <div className="search-container">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                placeholder={t('winnerRunnerUp.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
                aria-label={t('winnerRunnerUp.searchPlaceholder')}
              />
              {searchTerm && (
                <button
                  className="clear-search-btn"
                  onClick={() => setSearchTerm('')}
                  title={t('winnerRunnerUp.clearSearch')}
                  aria-label={t('winnerRunnerUp.clearSearch')}
                >✕</button>
              )}
            </div>
            <div className="results-info">
              <span className="results-count">
                {t('winnerRunnerUp.showing')} <strong>{filteredResults.length}</strong> {t('winnerRunnerUp.of')} <strong>{constituencyResults.length}</strong> {t('winnerRunnerUp.constituencies')}
              </span>
            </div>
          </div>
        </div>

        {filteredResults.length === 0 ? (
          <div className="no-results">
            <span className="no-results-icon">📭</span>
            <h3>{t('winnerRunnerUp.noResultsFound')}</h3>
            <p>{t('winnerRunnerUp.noMatch')}</p>
            <button className="reset-btn" onClick={() => setSearchTerm('')}>
              {t('winnerRunnerUp.clearSearch')}
            </button>
          </div>
        ) : (
          <div className="results-grid">
            {filteredResults.map((c, idx) => {
              const { constituencyName = t('winnerRunnerUp.unknown'), constituencyCode = '', candidates = [] } = c || {};
              const { winner, runnerUp } = getWinnerAndRunnerUp(candidates);

              return (
                <div key={`${constituencyCode}-${constituencyName}-${idx}`} className="constituency-card">
                  <div className="constituency-header">
                    {constituencyCode && <span className="const-code">{constituencyCode}</span>}
                    <h3 className="const-name">{constituencyName}</h3>
                  </div>
                  <div className="candidates-container">
                    {/* Winner Card */}
                    <div
                      className="candidate-card winner-card"
                      style={{ cursor: 'pointer' }}
                      onClick={() => winner && navigate(`/candidate/${winner.candidateId}`)}
                      tabIndex={0}
                      role="button"
                      onKeyPress={e => {
                        if ((e.key === 'Enter' || e.key === ' ') && winner) {
                          navigate(`/candidate/${winner.candidateId}`);
                        }
                      }}
                    >
                      <div className="card-badge winner-badge">{t('winnerRunnerUp.winner')}</div>
                      <div className="candidate-photo-wrapper">
                        <img
                          src={resolvePhoto(winner)}
                          alt={winner?.candidateName || winner?.name || t('winnerRunnerUp.unknown')}
                          className="candidate-photo"
                          onError={e => { e.target.onerror = null; e.target.src = fallbackPhoto; }}
                        />
                      </div>
                      <h4 className="candidate-name">{winner?.candidateName || winner?.name || t('winnerRunnerUp.unknown')}</h4>
                      <div className="party-info">
                        <img
                          src={resolveSymbol(winner)}
                          alt={t('winnerRunnerUp.partySymbol')}
                          className="party-symbol"
                          onError={e => { e.target.onerror = null; e.target.src = fallbackSymbol; }}
                        />
                        <span className="party-name">{winner?.partyShortName || winner?.partyName || winner?.party || 'IND'}</span>
                      </div>
                      <div className="vote-count">
                        <span className="vote-label">{t('winnerRunnerUp.votes')}</span>
                        <span className="vote-number">{winner?.latestVotes?.toLocaleString() || '0'}</span>
                      </div>
                    </div>
                    {/* Runner-up Card */}
                    <div
                      className="candidate-card runnerup-card"
                      style={{ cursor: 'pointer' }}
                      onClick={() => runnerUp && navigate(`/candidate/${runnerUp.candidateId}`)}
                      tabIndex={0}
                      role="button"
                      onKeyPress={e => {
                        if ((e.key === 'Enter' || e.key === ' ') && runnerUp) {
                          navigate(`/candidate/${runnerUp.candidateId}`);
                        }
                      }}
                    >
                      <div className="card-badge runnerup-badge">{t('winnerRunnerUp.runnerUp')}</div>
                      <div className="candidate-photo-wrapper">
                        <img
                          src={resolvePhoto(runnerUp)}
                          alt={runnerUp?.candidateName || runnerUp?.name || t('winnerRunnerUp.unknown')}
                          className="candidate-photo"
                          onError={e => { e.target.onerror = null; e.target.src = fallbackPhoto; }}
                        />
                      </div>
                      <h4 className="candidate-name">{runnerUp?.candidateName || runnerUp?.name || t('winnerRunnerUp.unknown')}</h4>
                      <div className="party-info">
                        <img
                          src={resolveSymbol(runnerUp)}
                          alt={t('winnerRunnerUp.partySymbol')}
                          className="party-symbol"
                          onError={e => { e.target.onerror = null; e.target.src = fallbackSymbol; }}
                        />
                        <span className="party-name">{runnerUp?.partyShortName || runnerUp?.partyName || runnerUp?.party || 'IND'}</span>
                      </div>
                      <div className="vote-count">
                        <span className="vote-label">{t('winnerRunnerUp.votes')}</span>
                        <span className="vote-number">{runnerUp?.latestVotes?.toLocaleString() || '0'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default WinnerRunnerUpPage;
